/**
 * Smartwatch Replay Page - Ultra-simplified interface for Apple Watch / Wear OS
 * 
 * Features:
 * - Minimal UI optimized for small screens (38-45mm)
 * - Large touch targets (minimum 44px)
 * - Single tap for normal clip, double tap for fast clip
 * - Auto-login via URL token parameter (?t=xxx)
 * - Session status indicator
 */
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Check, Loader2, AlertCircle, LogIn, Zap } from 'lucide-react';
import { useAuthStore } from '../store';
import api from '../services/api';
import { toast } from 'sonner';

const DOUBLE_TAP_DELAY = 300;

export default function WatchReplay() {
  const { user, isAuthenticated, login } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [activeSession, setActiveSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [clipType, setClipType] = useState(null);
  const [error, setError] = useState(null);
  const [tapCount, setTapCount] = useState(0);
  const tapTimeoutRef = useRef(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(false);

  // Auto-login via URL token
  useEffect(() => {
    const attemptAutoLogin = async () => {
      const token = searchParams.get('t');
      
      // Skip if no token, already authenticated, or already attempted
      if (!token || isAuthenticated || autoLoginAttempted) {
        setAutoLoginAttempted(true);
        return;
      }
      
      setAutoLoginInProgress(true);
      
      try {
        console.log('[Watch] Attempting auto-login with token');
        const response = await api.get(`/auth/watch-login?t=${token}`);
        
        if (response.data && response.data.token && response.data.user) {
          // Save the JWT token and user to store
          login(response.data.user, response.data.token);
          console.log('[Watch] Auto-login successful');
          toast.success('¡Bienvenido!', { duration: 2000 });
        }
      } catch (err) {
        console.error('[Watch] Auto-login failed:', err);
        const errorMsg = err.response?.data?.detail || 'Error de auto-login';
        if (errorMsg.includes('expirado')) {
          toast.error('Link expirado. Genera uno nuevo desde tu celular.');
        } else {
          toast.error('Error de auto-login');
        }
      } finally {
        setAutoLoginAttempted(true);
        setAutoLoginInProgress(false);
      }
    };
    
    attemptAutoLogin();
  }, [searchParams, isAuthenticated, autoLoginAttempted, login]);

  // Check for active session
  useEffect(() => {
    const checkSession = async () => {
      if (!isAuthenticated || !user) {
        setCheckingSession(false);
        setActiveSession(null);
        return;
      }

      try {
        const response = await api.get('/session');
        
        if (response.data && response.data.active && response.data.court_id) {
          let courtNumber = '?';
          if (response.data.club?.courts) {
            const court = response.data.club.courts.find(c => c.id === response.data.court_id);
            if (court) {
              courtNumber = court.court_number;
            }
          }
          
          setActiveSession({
            ...response.data,
            court_number: courtNumber,
            club_name: response.data.club?.name || 'Club'
          });
          setError(null);
        } else {
          setActiveSession(null);
          setError('no_session');
        }
      } catch (err) {
        console.error('Session check error:', err);
        setActiveSession(null);
        setError('no_session');
      } finally {
        setCheckingSession(false);
      }
    };

    // Only check session after auto-login attempt is complete
    if (autoLoginAttempted && !autoLoginInProgress) {
      checkSession();
      const interval = setInterval(checkSession, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user, autoLoginAttempted, autoLoginInProgress]);

  // Create clip
  const createClip = async (type) => {
    if (!activeSession || isLoading) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/clips/quick', {
        is_fast: type === 'fast',
        speed_multiplier: type === 'fast' ? 2.0 : 1.0
      });

      if (response.data && response.data.clip_id) {
        setClipType(type);
        setShowSuccess(true);
        
        setTimeout(() => {
          setShowSuccess(false);
          setClipType(null);
        }, 3000);
      }
    } catch (err) {
      console.error('Error creating clip:', err);
      const errorMsg = err.response?.data?.detail || 'Error';
      setError(errorMsg.includes('segundos') ? 'cooldown' : 'error');
      
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tap with double-tap detection
  const handleTap = () => {
    if (isLoading || !activeSession) return;

    setTapCount(prev => prev + 1);

    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    tapTimeoutRef.current = setTimeout(() => {
      if (tapCount === 0) {
        createClip('normal');
      } else {
        createClip('fast');
      }
      setTapCount(0);
    }, DOUBLE_TAP_DELAY);
  };

  // Show loading while auto-login is in progress
  if (autoLoginInProgress) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-2">
        <style>{`[class*="emergent"],[id*="emergent"],[data-emergent],a[href*="emergent"]{transform:scale(0.5)!important;opacity:0.3!important;bottom:2px!important;right:2px!important;}`}</style>
        <Loader2 className="w-10 h-10 text-primary animate-spin mb-2" />
        <p className="text-gray-400 text-xs text-center">
          Iniciando sesión...
        </p>
      </div>
    );
  }

  // Not authenticated (and auto-login already attempted)
  if (!isAuthenticated && autoLoginAttempted) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-2">
        <style>{`[class*="emergent"],[id*="emergent"],[data-emergent],a[href*="emergent"]{transform:scale(0.5)!important;opacity:0.3!important;bottom:2px!important;right:2px!important;}`}</style>
        <LogIn className="w-10 h-10 text-gray-500 mb-2" />
        <p className="text-gray-400 text-xs text-center">
          Inicia sesión en tu celular primero
        </p>
        <p className="text-gray-600 text-[9px] text-center mt-2 px-4">
          Abre Clipper en tu celular, toca el ícono de reloj y comparte el link
        </p>
      </div>
    );
  }

  // Loading session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  // No active session - Friendly message
  if (!activeSession) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-3">
        <style>{`[class*="emergent"],[id*="emergent"],[data-emergent],a[href*="emergent"]{transform:scale(0.5)!important;opacity:0.3!important;bottom:2px!important;right:2px!important;}`}</style>
        <AlertCircle className="w-8 h-8 text-yellow-500 mb-2" />
        <p className="text-yellow-400 text-[11px] text-center font-medium leading-tight">
          Sin sesión activa
        </p>
        <p className="text-gray-400 text-[9px] text-center mt-1 leading-tight px-2">
          Escanea el QR de la cancha en tu celular
        </p>
        <p className="text-gray-600 text-[8px] text-center mt-2">
          📱 Luego regresa aquí
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-2 select-none relative">
      {/* Hide/minimize Emergent badge on watch page */}
      <style>{`
        [class*="emergent"], 
        [id*="emergent"],
        [data-emergent],
        a[href*="emergent"] {
          transform: scale(0.5) !important;
          opacity: 0.3 !important;
          bottom: 2px !important;
          right: 2px !important;
        }
      `}</style>
      
      {/* Session Info - Very compact */}
      <div className="text-center mb-3">
        <p className="text-green-400 text-[10px] font-medium">
          🏟️ Cancha {activeSession.court_number}
        </p>
        <p className="text-gray-600 text-[8px] truncate max-w-[120px]">
          {activeSession.club_name}
        </p>
      </div>

      {/* Main Button */}
      <motion.button
        className={`
          w-24 h-24 rounded-full
          flex flex-col items-center justify-center
          transition-all duration-200 active:scale-95
          ${isLoading ? 'bg-gray-700' : 
            showSuccess ? (clipType === 'fast' ? 'bg-blue-600' : 'bg-green-600') :
            error ? 'bg-red-600' : 'bg-green-600'}
          shadow-lg shadow-green-500/20
        `}
        onClick={handleTap}
        disabled={isLoading}
        whileTap={{ scale: 0.9 }}
      >
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </motion.div>
          ) : showSuccess ? (
            <motion.div
              key="success"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="text-center"
            >
              <Check className="w-8 h-8 text-white mx-auto" />
              <span className="text-[10px] text-white font-medium">
                {clipType === 'fast' ? '60s 2x' : '30s'}
              </span>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="text-center"
            >
              <AlertCircle className="w-8 h-8 text-white mx-auto" />
              <span className="text-[10px] text-white">
                {error === 'cooldown' ? 'Espera...' : 'Error'}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="ready"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-center"
            >
              <Video className="w-10 h-10 text-white mx-auto" />
              <span className="text-[10px] text-white/80 font-medium mt-1">
                REPLAY
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Tap indicator */}
      <AnimatePresence>
        {tapCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-2 flex items-center gap-1 text-blue-400"
          >
            <Zap className="w-3 h-3" />
            <span className="text-[10px]">2x</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions - Very small */}
      {!isLoading && !showSuccess && !error && (
        <div className="mt-3 text-center">
          <p className="text-gray-500 text-[8px]">
            1 tap = 30s • 2 taps = 60s 2x
          </p>
        </div>
      )}

      {/* User indicator */}
      <div className="absolute bottom-2 left-0 right-0 text-center">
        <p className="text-gray-700 text-[8px]">
          {user?.first_name || user?.email?.split('@')[0] || 'Usuario'}
        </p>
      </div>
    </div>
  );
}
